<template>
    <m-pageframe title="Order Confirmation Demo Gateway">
        <div class="demo-intro-container">
            <v-card class="content-card">
                <v-card-text class="pa-8">
                    <div class="hero-section">
                        <v-icon
                            size="60"
                            color="primary"
                            class="mb-2"
                            aria-hidden="true"
                        >mdi-presentation</v-icon>
                        <h1 class="text-h4 font-weight-bold mb-1">Order Confirmation Experience Demo</h1>
                        <p class="text-h6 mb-4 font-weight-light">
                            Interactive demonstration of written consent collection during the order confirmation
                            process
                        </p>
                    </div>

                    <div class="demo-overview">
                        <section class="overview-grid">
                            <div class="overview-section">
                                <h3 class="text-h5 mb-3">Business Impact</h3>
                                <div class="benefit-list">
                                    <div
                                        v-for="benefit in benefits"
                                        :key="benefit.title"
                                        class="benefit-item"
                                    >
                                        <v-icon
                                            color="primary"
                                            class="benefit-icon"
                                            aria-hidden="true"
                                        >{{ benefit.icon }}</v-icon>
                                        <div>
                                            <strong>{{ benefit.title }}</strong><br>
                                            <span>{{ benefit.description }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="overview-section">
                                <h3 class="text-h5 mb-3">Experience Flow</h3>
                                <div class="flow-steps">
                                    <div
                                        v-for="step in flowSteps"
                                        :key="step.number"
                                        class="flow-step"
                                    >
                                        <div class="step-number">{{ step.number }}</div>
                                        <div class="step-content">
                                            <strong>{{ step.title }}</strong><br>
                                            <span>{{ step.description }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="compliance-section">
                            <h3 class="text-h5 mb-3">Regulatory Compliance</h3>
                            <div class="compliance-grid">
                                <div
                                    v-for="item in complianceItems"
                                    :key="item.title"
                                    class="compliance-item"
                                >
                                    <v-icon
                                        color="secondary"
                                        size="32"
                                        aria-hidden="true"
                                    >{{ item.icon }}</v-icon>
                                    <div>
                                        <strong>{{ item.title }}</strong><br>
                                        <span v-html="item.description"></span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div class="text-center">
                            <v-btn
                                color="primary"
                                large
                                @click="handleStartDemo"
                            >
                                <v-icon
                                    left
                                    size="24"
                                    aria-hidden="true"
                                >mdi-play</v-icon>
                                Start Customer Experience Demo
                            </v-btn>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </div>
    </m-pageframe>
</template>

<script>
export default {
    name: 'OrderConfirmationDemoIntro',

    data() {
        return {
            benefits: [
                {
                    icon: 'mdi-trending-up',
                    title: '2.1x Higher Order Attribution',
                    description: 'Research that shows that consumers with consent on file in RedChirp have 2.1x the attributed orders to those who do not'
                },
                {
                    icon: 'mdi-account-check',
                    title: 'Enhanced Customer Trust',
                    description: 'Transparent consent process builds customer confidence and improves long-term relationships'
                },
                {
                    icon: 'mdi-emoticon-happy-outline',
                    title: 'Replace Consent Anxiety with Action',
                    description: 'If you\'re worried about consent, RedChirp provides straightforward flows and documentation so you can act confidently'
                }
            ],
            flowSteps: [
                {
                    number: 1,
                    title: 'Order Placed',
                    description: 'Customer completes purchase and chooses shipping'
                },
                {
                    number: 2,
                    title: 'SMS Notification',
                    description: 'Automated text with secure link to confirm order details'
                },
                {
                    number: 3,
                    title: 'Order Confirmation',
                    description: 'Customer verifies details or requests changes, providing express consent along the way'
                }
            ],
            complianceItems: [
                {
                    icon: 'mdi-shield-sync',
                    title: 'May Help Meet Compliance Goals',
                    description: 'Helps you gather consent and provides documentation to support TCPA and applicable state law compliance'
                },
                {
                    icon: 'mdi-file-document-check',
                    title: 'Complete Audit Trail',
                    description: 'Automated documentation of consent collection may reduces risk and ensures records of consent are maintained'
                }
            ]
        }
    },

    methods: {
        handleStartDemo() {
            const route = this.$router.resolve({ name: 'order-confirmation-demo-start' });
            window.open(route.href, '_blank');
        }
    },

    mounted() {
        document.title = "Order Confirmation Demo | RedChirp";
    }
}
</script>

<style lang="scss" scoped>
.demo-intro-container {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 100vh;
    padding: 2rem;

    .content-card {
        position: relative;
        max-width: min(1200px, 95%);
        width: 100%;
        border-radius: 1rem !important;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
        border-top: 4px solid var(--v-primary-base) !important;
        text-align: center;
    }

    .hero-section {
        margin-bottom: 3rem;
    }

    .demo-overview {
        text-align: left;

        .overview-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            margin-bottom: 3rem;

            @media (max-width: 900px) {
                grid-template-columns: 1fr;
                gap: 2rem;
            }
        }

        .overview-section {
            .benefit-list {
                display: flex;
                flex-direction: column;
                gap: 1.5rem;

                .benefit-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;

                    .benefit-icon {
                        font-size: 1.5rem !important;
                        margin-top: 0.25rem;
                    }

                    div {
                        line-height: 1.4;

                        strong {
                            color: var(--v-primary-base);
                            font-size: 1.1rem;
                        }

                        span {
                            color: #666;
                            font-size: 0.95rem;
                        }
                    }
                }
            }

            .flow-steps {
                display: flex;
                flex-direction: column;
                gap: 1.5rem;

                .flow-step {
                    display: flex;
                    align-items: center;
                    gap: 1rem;

                    .step-number {
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        background: var(--v-primary-base);
                        color: white;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: bold;
                        flex-shrink: 0;
                    }

                    .step-content {
                        line-height: 1.4;

                        strong {
                            color: var(--v-primary-base);
                            font-size: 1.1rem;
                        }

                        span {
                            color: #666;
                            font-size: 0.95rem;
                        }
                    }
                }
            }
        }
    }

    .compliance-section {
        background: var(--color-bg-shaded-light);
        border-radius: 0.75rem;
        padding: 1.5rem 2rem 2rem 2rem;
        margin-bottom: 3rem;

        .compliance-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin-top: 1rem;

            .compliance-item {
                --v-anchor-base: var(--v-secondary-base);

                display: flex;
                align-items: flex-start;
                gap: 1rem;

                div {
                    line-height: 1.4;

                    strong {
                        color: var(--v-secondary-base);
                        font-size: 1.1rem;
                    }

                    span {
                        color: #666;
                        font-size: 0.95rem;
                    }
                }
            }
        }
    }
}
</style>